import { z } from "zod";
import { idSchema, optionalText } from "./fields";

export const damagePolicySchema = z.enum(["ownerWaives", "borrowerPays"]);

const boundedInt = (label: string, min: number, max: number) =>
  z
    .number({ error: `${label} must be a number.` })
    .int(`${label} must be a whole number.`)
    .min(min, `${label} must be at least ${min}.`)
    .max(max, `${label} must be at most ${max}.`);

const boatFields = {
  name: z.string().trim().min(1, "Boat name is required.").max(100),
  makeModel: optionalText(100),
  lengthFeet: z.number().positive().max(500).optional(),
  storageLocation: optionalText(200),
  /** Named as the Owner on each Boat Loan Agreement. */
  ownerName: z.string().trim().min(1, "Owner name is required.").max(100),
  damagePolicy: damagePolicySchema,
  description: optionalText(2000),
  /** Free-text rules shown to veterans before they book. */
  rules: z.string().trim().max(5000),
  capacity: boundedInt("Capacity", 1, 100),
  maxConsecutiveDays: boundedInt("Maximum consecutive days", 1, 60),
  minAdvanceDays: boundedInt("Minimum notice", 0, 365),
  maxAdvanceDays: boundedInt("Booking horizon", 1, 730),
  isActive: z.boolean(),
};

export const boatSchema = z.object({
  id: idSchema,
  ...boatFields,
  /** When the current photo was uploaded; absent when the boat has no photo. */
  photoUpdatedAt: z.date().optional(),
  updatedAt: z.date(),
});
export type Boat = z.infer<typeof boatSchema>;

export const boatInputSchema = z
  .object(boatFields)
  .refine((b) => b.minAdvanceDays <= b.maxAdvanceDays, {
    message: "Minimum notice cannot be longer than the booking horizon.",
    path: ["minAdvanceDays"],
  });
export type BoatInput = z.infer<typeof boatInputSchema>;
